import { Divider, Flex, Group, ScrollArea, Stack, TextInput } from '@mantine/core';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitiatives } from '../../../api/initiative-client/initiative-client.hooks';
import type { Initiative } from '../../../api/initiative-client/initiative-client.type';
import { useOutcomes } from '../../../api/strategy-client/strategy-client.hooks';
import type { Outcome } from '../../../api/strategy-client/strategy-client.type';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { HEIGHT_BREAKPOINTS } from '../../../styles/breakpoints';
import { baseWhite, inkLight, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Checkbox } from '../../../ui-library/checkbox/checkbox';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { useEpicsQuery } from '../strategy.hooks';
import type { StrategyEntityType } from '../strategy.type';
import { useEpicsSearch, useInitiativesSearch, useOutcomesSearch } from './strategy-entity-modal-step-two.hooks';
import { StrategyEntityModalMode } from './strategy-entity-modal.type';

type Props = {
  mode: StrategyEntityModalMode;
  entityType: StrategyEntityType;
  selectedEpics: Epic[];
  setSelectedEpics: React.Dispatch<React.SetStateAction<Epic[]>>;
  selectedInitiatives: Initiative[];
  setSelectedInitiatives: React.Dispatch<React.SetStateAction<Initiative[]>>;
  selectedOutcomes: Outcome[];
  setSelectedOutcomes: React.Dispatch<React.SetStateAction<Outcome[]>>;
  onBack: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
};

export function StepTwo({
  mode,
  entityType,
  selectedEpics,
  setSelectedEpics,
  selectedInitiatives,
  setSelectedInitiatives,
  selectedOutcomes,
  setSelectedOutcomes,
  onBack,
  onCancel,
  onSubmit,
  isSubmitting,
}: Props) {
  const i18n = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const epicsFromStore = useStrategyStore((state) => state.epics);
  const { height } = useWindowSize();

  // Fetch data based on entity type
  const { epics: epicsFromQuery = [] } = useEpicsQuery({ enabled: entityType === 'initiative' });
  const { initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id && (entityType === 'outcome' || entityType === 'goal'),
  } as any);
  const { outcomes = [] } = useOutcomes({
    enabled: entityType === 'goal',
  } as any);

  // Use store epics for initiatives, fetched data for others
  const epics = entityType === 'initiative' ? (epicsFromStore as any) : epicsFromQuery;

  const { filteredEpics, handleSearchChange: handleEpicsSearch } = useEpicsSearch(epics);
  const { filteredInitiatives, handleSearchChange: handleInitiativesSearch } = useInitiativesSearch(initiatives);
  const { filteredOutcomes, handleSearchChange: handleOutcomesSearch } = useOutcomesSearch(outcomes);

  const handleEpicCheckboxChange = (epic: Epic, event: ChangeEvent<HTMLInputElement>) => {
    setSelectedEpics((prev) => (event.target.checked ? [...prev, epic] : prev.filter((e) => e.id !== epic.id)));
  };

  const handleInitiativeCheckboxChange = (initiative: Initiative, event: ChangeEvent<HTMLInputElement>) => {
    setSelectedInitiatives((prev) =>
      event.target.checked ? [...prev, initiative] : prev.filter((i) => i.id !== initiative.id),
    );
  };

  const handleOutcomeCheckboxChange = (outcome: Outcome, event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOutcomes((prev) =>
      event.target.checked ? [...prev, outcome] : prev.filter((o) => o.id !== outcome.id),
    );
  };

  const getLabels = () => {
    if (entityType === 'initiative') {
      return {
        description: i18n.t('strategy.initiatives.form.epic_description'),
        searchPlaceholder: i18n.t('strategy.initiatives.form.search_and_select_epics'),
        noItemsFound: i18n.t('strategy.filters.no_epics_found'),
        back: i18n.t('strategy.initiatives.form.back'),
        cancel: i18n.t('strategy.initiatives.form.cancel'),
        submit:
          mode === 'edit'
            ? i18n.t('strategy.initiatives.form.save_changes')
            : i18n.t('strategy.initiatives.form.complete_and_create'),
      };
    }

    if (entityType === 'outcome') {
      return {
        description: 'Select the initiatives that contribute to this objective.',
        searchPlaceholder: 'Search and select initiatives',
        noItemsFound: 'No initiatives found',
        back: 'Back',
        cancel: 'Cancel',
        submit: mode === 'edit' ? 'Save Changes' : 'Complete and Create',
      };
    }

    // goal
    return {
      description: 'Select the objectives that contribute to this strategic goal.',
      searchPlaceholder: 'Search and select objectives',
      noItemsFound: 'No objectives found',
      back: 'Back',
      cancel: 'Cancel',
      submit: mode === 'edit' ? 'Save Changes' : 'Complete and Create',
    };
  };

  const labels = getLabels();

  const renderContent = () => {
    if (entityType === 'initiative') {
      return (
        <>
          <Text>{labels.description}</Text>

          <TextInput
            data-bf="strategy-modal-input-search"
            placeholder={labels.searchPlaceholder}
            radius="xl"
            size="sm"
            leftSection={<Icon name="search" size={16} color={inkLight} />}
            onChange={(event) => {
              handleEpicsSearch(event.currentTarget.value);
            }}
          />

          <Divider />

          <ScrollArea type="always" h={height > parseInt(HEIGHT_BREAKPOINTS.md, 10) ? 336 : 240} scrollbarSize={8}>
            <Stack gap={24}>
              {filteredEpics.length === 0 ? (
                <Text size="small" color={inkLight} style={{ textAlign: 'center' }}>
                  {labels.noItemsFound}
                </Text>
              ) : (
                filteredEpics.map((epic) => (
                  <Stack gap={8} key={epic.id}>
                    <Checkbox
                      label={
                        <Text size="small" color={secondaryBase} style={{ lineHeight: '20px' }}>
                          {epic.title}
                        </Text>
                      }
                      checked={selectedEpics.some((e) => e.id === epic.id)}
                      onChange={(event) => handleEpicCheckboxChange(epic, event)}
                    />
                    <Text size="small" style={{ width: 420 }}>
                      {epic.name}
                    </Text>
                  </Stack>
                ))
              )}
            </Stack>
          </ScrollArea>
        </>
      );
    }

    if (entityType === 'outcome') {
      return (
        <>
          <Text>{labels.description}</Text>

          <TextInput
            data-bf="strategy-modal-input-search"
            placeholder={labels.searchPlaceholder}
            radius="xl"
            size="sm"
            leftSection={<Icon name="search" size={16} color={inkLight} />}
            onChange={(event) => {
              handleInitiativesSearch(event.currentTarget.value);
            }}
          />

          <Divider />

          <ScrollArea type="always" h={height > parseInt(HEIGHT_BREAKPOINTS.md, 10) ? 336 : 240} scrollbarSize={8}>
            <Stack gap={24}>
              {filteredInitiatives.length === 0 ? (
                <Text size="small" color={inkLight} style={{ textAlign: 'center' }}>
                  {labels.noItemsFound}
                </Text>
              ) : (
                filteredInitiatives.map((initiative) => (
                  <Stack gap={8} key={initiative.id}>
                    <Checkbox
                      label={
                        <Text size="small" color={secondaryBase} style={{ lineHeight: '20px' }}>
                          {initiative.name}
                        </Text>
                      }
                      checked={selectedInitiatives.some((i) => i.id === initiative.id)}
                      onChange={(event) => handleInitiativeCheckboxChange(initiative, event)}
                    />
                    <Text size="small" style={{ width: 420 }}>
                      {initiative.description}
                    </Text>
                  </Stack>
                ))
              )}
            </Stack>
          </ScrollArea>
        </>
      );
    }

    // goal
    return (
      <>
        <Text>{labels.description}</Text>

        <TextInput
          data-bf="strategy-modal-input-search"
          placeholder={labels.searchPlaceholder}
          radius="xl"
          size="sm"
          leftSection={<Icon name="search" size={16} color={inkLight} />}
          onChange={(event) => {
            handleOutcomesSearch(event.currentTarget.value);
          }}
        />

        <Divider />

        <ScrollArea type="always" h={height > parseInt(HEIGHT_BREAKPOINTS.md, 10) ? 336 : 240} scrollbarSize={8}>
          <Stack gap={24}>
            {filteredOutcomes.length === 0 ? (
              <Text size="small" color={inkLight} style={{ textAlign: 'center' }}>
                {labels.noItemsFound}
              </Text>
            ) : (
              filteredOutcomes.map((outcome) => (
                <Stack gap={8} key={outcome.id}>
                  <Checkbox
                    label={
                      <Text size="small" color={secondaryBase} style={{ lineHeight: '20px' }}>
                        {outcome.name}
                      </Text>
                    }
                    checked={selectedOutcomes.some((o) => o.id === outcome.id)}
                    onChange={(event) => handleOutcomeCheckboxChange(outcome, event)}
                  />
                  <Text size="small" style={{ width: 420 }}>
                    {outcome.description}
                  </Text>
                </Stack>
              ))
            )}
          </Stack>
        </ScrollArea>
      </>
    );
  };

  return (
    <Stack mih={0}>
      {renderContent()}

      <Divider />

      <Flex justify="space-between" style={{ flexShrink: 0 }}>
        <Button
          data-bf="strategy-modal-btn-back"
          variant="outline"
          radius="xl"
          onClick={onBack}
          leftSection={<Icon name="arrow_back" size={16} color={inkLight} pointer />}
        >
          {labels.back}
        </Button>
        <Group gap={16}>
          <Button data-bf="strategy-modal-btn-step2-cancel" variant="outline" radius="xl" onClick={onCancel}>
            {labels.cancel}
          </Button>
          <Button
            data-bf="strategy-modal-btn-submit"
            radius="xl"
            onClick={onSubmit}
            rightSection={<Icon name="task_alt" size={16} color={baseWhite} pointer />}
            loading={isSubmitting}
            loaderProps={{ size: 'xs' }}
          >
            {labels.submit}
          </Button>
        </Group>
      </Flex>
    </Stack>
  );
}
